import type { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine, EditLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Update_StackIconColor } from "@mirage/analytics/events/types/update_stack_icon_color";
import {
  useFeatureFlagValue,
  useFeatureFlagValueBool,
} from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { stackDerivePAPProps } from "@mirage/service-stacks";
import { useStackPaths } from "@mirage/stacks/paths";
import i18n from "@mirage/translations";
import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { MenuItem } from "../ActionsMenu";
import { ColorPicker } from "../ColorPicker";
import { HexColorPicker } from "../ColorPicker/HexColorPicker";
import { CustomColorPicker } from "../CustomColorPicker";
import { stackHasWritePermissions } from "../Helpers/Utils";
import {
  useUpdateStackBanner,
  useUpdateStackColorIndex,
  useUpdateStackWithCustomColor,
} from "../hooks";
import { getDefaultColorIndex } from "../themes";
import { isValidHexColor } from "../utils";
import styles from "./StackNavBar.module.css";

export const StackNavBar = ({ stack }: { stack: stacks.Stack | null }) => {
  const stackPaths = useStackPaths();
  const isStackBannerEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2025_06_24_fss_stacks"),
  );
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const updateStackBanner = useUpdateStackBanner(stack);
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // Reset the input value to allow re-uploading the same file
    if (!file) return;
    await updateStackBanner(file);
  };
  const { reportPapEvent } = useMirageAnalyticsContext();
  const hasEditPermission = stack && stackHasWritePermissions(stack);
  const onColorIndexChange = useUpdateStackColorIndex(stack);
  const onCustomColorChange = useUpdateStackWithCustomColor(stack);
  const isCustomStackColorFeatureEnabled = useFeatureFlagValueBool(
    "dash_web_2025_03_12_custom_stack_color",
  );

  const colorPickerButtonRef = useRef(null);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const handleColorPickerClose = () => {
    setIsColorPickerOpen(false);
  };
  const handleUpdateStackCustomColor = (customHexColor: string) => {
    onCustomColorChange(customHexColor);
    setIsColorPickerOpen(false);
  };

  const handleMenuItemSelect = (menuItem: string) => {
    const [key, value] = menuItem.split("|");
    if (key === MenuItem.COLOR) {
      onColorIndexChange(Number(value));
      if (stack) {
        reportPapEvent(
          PAP_Update_StackIconColor({
            ...stackDerivePAPProps(stack),
            actionSurfaceComponent: "stack_header",
            featureLine: "stacks",
            stackIconUpdate: "regular",
          }),
        );
      }
    } else if (key === MenuItem.CUSTOM_COLOR) {
      if (isValidHexColor(value)) {
        onCustomColorChange(value);
      } else {
        setIsColorPickerOpen(true);
      }
    }
  };

  const handleToggle = (isOpen: boolean) => {
    if (!stack || !stack?.namespace_id) return;
    if (isOpen && isCustomStackColorFeatureEnabled) {
      setIsColorPickerOpen(false);
    }
  };

  return (
    <div className={styles.stackNavBar} ref={colorPickerButtonRef}>
      <div className={styles.stackNavBarLeft}>
        <NavLink to={stackPaths.stacks()}>
          <Button
            variant="borderless"
            withIconStart={<UIIcon src={ChevronLeftLine} />}
          >
            {i18n.t("all_stacks")}
          </Button>
        </NavLink>
      </div>
      {hasEditPermission && (
        <>
          <Menu.Wrapper
            onSelection={handleMenuItemSelect}
            onToggle={(event) => handleToggle(event.isOpen)}
          >
            {({ getContentProps, getTriggerProps }) => (
              <>
                <Button
                  withIconStart={<UIIcon src={EditLine} />}
                  variant="borderless"
                  className={styles.stackNavBarEditButton}
                  {...getTriggerProps()}
                >
                  {i18n.t("stack_banner_edit_button")}
                </Button>
                <Menu.Content {...getContentProps()}>
                  <Menu.Segment withLabel={i18n.t("stack_theme")}>
                    <ColorPicker
                      menuKey={MenuItem.COLOR}
                      colorIndex={
                        stack?.stack_data?.color_index || getDefaultColorIndex()
                      }
                      stackNamespaceId={stack?.namespace_id}
                    />
                    {isCustomStackColorFeatureEnabled && (
                      <CustomColorPicker
                        menuKey={MenuItem.CUSTOM_COLOR}
                        customHexColor={stack?.stack_data?.custom_color}
                        stackNamespaceId={stack?.namespace_id}
                        colorIndex={
                          stack?.stack_data?.color_index ||
                          getDefaultColorIndex()
                        }
                      />
                    )}
                    {isStackBannerEnabled && (
                      <div className={styles.stackBannerEditButtonWrapper}>
                        <Button
                          className={styles.stackBannerEditButton}
                          variant="outline"
                          onClick={() => {
                            bannerInputRef.current?.click();
                          }}
                        >
                          {i18n.t("stack_banner_upload_button")}
                        </Button>
                      </div>
                    )}
                  </Menu.Segment>
                </Menu.Content>
              </>
            )}
          </Menu.Wrapper>
          <input
            type="file"
            accept="image/png, image/gif, image/jpeg, image/jpg"
            hidden
            ref={bannerInputRef}
            onChangeCapture={handleFileUpload}
          />
          {isCustomStackColorFeatureEnabled && (
            <HexColorPicker
              anchorRef={colorPickerButtonRef}
              isPortaled
              onClose={handleColorPickerClose}
              onSave={handleUpdateStackCustomColor}
              open={isColorPickerOpen}
              color={stack?.stack_data?.custom_color}
              key={stack?.namespace_id}
            />
          )}
        </>
      )}
    </div>
  );
};
